/**
 * The promotion gate's verdict, and the holdout it opens. Backlog tasks 85 and 89's verdict panel.
 *
 * `Validation-Methodology.md`: *"a strategy cannot be deployed to paper until it passes **every**
 * criterion."* This screen shows which ones it passed, which it failed, and — the part that is easy
 * to render wrongly — which were never evaluated at all.
 *
 * ## 🔴 `unknown` gets its own column, not a shrug
 *
 * A criterion nobody could evaluate is not a criterion that passed, and it is not a criterion that
 * failed either. Rendering the twelve as a pass/fail pair would make *"no walk-forward run exists"*
 * look identical to *"the folds were inconsistent"*, and the fix for those is not the same fix. So
 * every row carries its verdict word, and the summary counts all three.
 *
 * ## The verdict is never tinted
 *
 * *"A red verdict panel makes bad news scannable and therefore skippable."* `VerdictPanel` is
 * uncoloured by design and it is used as-is. The colour in this screen is on the per-criterion
 * verdict words, where it is paired with the word itself — colour never means anything alone.
 *
 * ## Unlocking is an action with a consequence, so it says the consequence
 *
 * The holdout is used **once**. The button says so before it is pressed, not after — and it is
 * disabled with a reason rather than hidden, because a control that vanishes teaches nothing about
 * why it is unavailable.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import type { HoldoutResponse, ValidationResponse } from '../api/generated';
import { Button } from '../design/components/controls/Button.jsx';
import { DataTable } from '../design/components/data/DataTable.jsx';
import { EmptyState } from '../design/components/messages/EmptyState.jsx';
import { ErrorPanel } from '../design/components/messages/ErrorPanel.jsx';
import { MetricTile } from '../design/components/data/MetricTile.jsx';
import { Num } from '../design/components/data/Num.jsx';
import { VerdictPanel } from '../design/components/messages/VerdictPanel.jsx';

type Criterion = {
  id?: string;
  name?: string;
  verdict?: string;
  threshold?: string;
  actual?: string;
  reason?: string;
};

/** The three verdicts, and the token each is spelled in. Never colour alone — the word is there. */
const TONE: Record<string, string> = {
  pass: 'var(--profit)',
  fail: 'var(--loss)',
  unknown: 'var(--text-tertiary)',
};

export function GatePanel({
  strategyVersionId,
  timeframe,
}: {
  strategyVersionId: number;
  /*
    🔴 **The rung the open run is at, and the gate is run at the same one.** A verdict is about a
    candidate *at a rung* — the fleet deploys at one — and before this the gate took the newest run
    of each kind whatever its timeframe, which produced *"38 total, 164 out-of-sample"* on
    production: a 4h in-sample against 1h folds. Passing the report's own rung means the verdict
    below the report is about the run above it.
  */
  timeframe: string;
}): React.JSX.Element {
  const client = useQueryClient();

  const validations = useQuery({
    queryKey: ['validations', strategyVersionId],
    queryFn: () => api.research.listValidations({ strategyVersionId }),
  });
  const holdout = useQuery({
    queryKey: ['holdout', strategyVersionId],
    queryFn: async () => {
      try {
        return await api.research.getHoldout({ strategyVersionId });
      } catch {
        // 404 is the ordinary case: most versions have no holdout declared. It is not an error
        // state and must not render as one.
        return null;
      }
    },
  });

  const evaluate = useMutation({
    mutationFn: () =>
      api.research.createValidation({
        requestBody: { strategy_version_id: strategyVersionId, timeframe },
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['validations', strategyVersionId] }),
  });
  const unlock = useMutation({
    mutationFn: (validationId: number) => api.research.unlockHoldout({ validationId }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['holdout', strategyVersionId] }),
  });

  /*
    ⚠ **The latest verdict AT THIS RUNG, not the latest verdict.** A version validated at 4h and
    then at 1h has two, and showing the 1h one under a 4h report is the same mismatch one level up.
    `null` when this rung has none, which reads as "not run here" rather than borrowing another
    rung's answer.
  */
  const latest: ValidationResponse | null =
    validations.data?.validations.find((item) => item.timeframe === timeframe) ?? null;
  const lock: HoldoutResponse | null = holdout.data ?? null;

  return (
    <section className="jmt-report-gate">
      <h2 className="jmt-section-title">Promotion gate — {timeframe}</h2>

      {latest === null ? (
        <div className="jmt-note">
          <EmptyState>
            The gate has not been run against this version at <strong>{timeframe}</strong>. It
            reads the metrics the completed runs at that rung already wrote — an in-sample
            backtest, a walk-forward, a sweep — so it computes nothing and takes no time. Criteria
            with no evidence come back <strong>unknown</strong>, which is not a pass.
          </EmptyState>
          <Button onClick={() => evaluate.mutate()} disabled={evaluate.isPending}>
            {evaluate.isPending ? 'Evaluating…' : `Run the gate at ${timeframe}`}
          </Button>
        </div>
      ) : (
        <Verdict
          validation={latest}
          lock={lock}
          onRerun={() => evaluate.mutate()}
          rerunning={evaluate.isPending}
          onUnlock={() => unlock.mutate(latest.id)}
          unlocking={unlock.isPending}
        />
      )}

      {evaluate.isError ? (
        <ErrorPanel
          message="The gate could not be evaluated."
          detail={String((evaluate.error as Error)?.message ?? '')}
        />
      ) : null}
      {unlock.isError ? (
        <ErrorPanel
          message="The holdout stayed locked."
          // The server's own sentence. It names which criteria are unmet, and restating it here
          // would be inventing a second explanation for one refusal.
          detail={String((unlock.error as Error)?.message ?? '')}
        />
      ) : null}
    </section>
  );
}

function Verdict({
  validation,
  lock,
  onRerun,
  rerunning,
  onUnlock,
  unlocking,
}: {
  validation: ValidationResponse;
  lock: HoldoutResponse | null;
  onRerun: () => void;
  rerunning: boolean;
  onUnlock: () => void;
  unlocking: boolean;
}): React.JSX.Element {
  const criteria = (validation.criteria ?? []) as Criterion[];
  const passed = criteria.filter((c) => c.verdict === 'pass').length;
  const failed = criteria.filter((c) => c.verdict === 'fail').length;
  const unknown = criteria.filter((c) => c.verdict === 'unknown').length;

  return (
    <>
      <VerdictPanel
        verdict={sentence(validation, passed, failed, unknown)}
        // Two or three, never ten — the component drops anything beyond three, and the reasons are
        // already ordered G1 first, which is the order they have to be fixed in.
        findings={(validation.reasons ?? []).slice(0, 3)}
        rules={criteria.filter((c) => c.verdict !== 'pass').map((c) => c.id ?? '?')}
      />

      <div className="jmt-report-grid">
        <MetricTile label="Passed" value={<Num value={String(passed)} />} context="of twelve" />
        <MetricTile
          label="Failed"
          value={<Num value={String(failed)} style={{ color: failed > 0 ? 'var(--loss)' : undefined }} />}
          context="criteria the evidence disqualifies"
        />
        <MetricTile
          label="Unevaluated"
          value={<Num value={String(unknown)} />}
          context="no evidence — not a pass"
        />
        <MetricTile
          label="Gate version"
          value={<Num value={validation.gate_version} />}
          context={`${validation.timeframe} · run ${validation.ran_at.slice(0, 10)}`}
        />
      </div>

      <DataTable
        density="compact"
        columns={[
          { key: 'id', label: 'ID', mono: true },
          { key: 'name', label: 'Criterion' },
          { key: 'threshold', label: 'Threshold' },
          { key: 'actual', label: 'Measured', align: 'right' },
          {
            key: 'verdict',
            label: 'Verdict',
            render: (row: Criterion) => (
              // The word carries the meaning and the colour reinforces it. Colour never means
              // anything alone — the design system's rule, and this is the table it exists for.
              <span style={{ color: TONE[row.verdict ?? 'unknown'] }}>{row.verdict}</span>
            ),
          },
          { key: 'reason', label: 'Why' },
        ]}
        rows={criteria.map((criterion) => ({ ...criterion, id: criterion.id ?? '?' }))}
      />

      <Holdout
        lock={lock}
        canUnlock={validation.pre_holdout_passed}
        onUnlock={onUnlock}
        unlocking={unlocking}
      />

      <Button variant="secondary" onClick={onRerun} disabled={rerunning}>
        {rerunning ? 'Evaluating…' : `Re-run the gate at ${validation.timeframe}`}
      </Button>
      <p className="jmt-note-text">
        A validation is append-only: re-running writes a new verdict rather than replacing this one.
        A strategy that passed has to stay identifiable as having passed the gate it passed.
      </p>
    </>
  );
}

/** The lock, its state, and the one action that changes it. */
function Holdout({
  lock,
  canUnlock,
  onUnlock,
  unlocking,
}: {
  lock: HoldoutResponse | null;
  canUnlock: boolean;
  onUnlock: () => void;
  unlocking: boolean;
}): React.JSX.Element {
  if (lock === null) {
    return (
      <div className="jmt-note">
        <EmptyState>
          No holdout is declared for this version, so G12 can never be answered and the gate can
          never fully pass. A holdout is declared through <code>POST /holdouts</code> at the{' '}
          <em>start</em> of the work — choosing it afterwards is choosing it with knowledge of the
          result.
        </EmptyState>
      </div>
    );
  }

  const window = `${lock.range_start.slice(0, 10)} → ${lock.range_end.slice(0, 10)}`;

  if (lock.state === 'consumed') {
    return (
      <div className="jmt-note">
        <p className="jmt-note-text">
          The holdout ({window}) was used on {lock.consumed_at?.slice(0, 10) ?? 'an unrecorded date'}{' '}
          by run #{lock.consumed_by_run_id ?? '?'}. It is in-sample data now. A second use would
          make it in-sample retrospectively, which is why there is no second unlock.
        </p>
      </div>
    );
  }

  if (lock.state === 'unlocked') {
    return (
      <div className="jmt-note">
        <p className="jmt-note-text">
          The holdout ({window}) is unlocked and unused. Queue one <code>holdout</code> run over
          exactly that range — a run over part of it consumes the whole lock and leaves the rest
          neither in-sample nor available.
        </p>
      </div>
    );
  }

  return (
    <div className="jmt-note">
      <p className="jmt-note-text">
        The holdout ({window}) is locked. Every run overlapping it is refused, whatever its kind —
        an unlock gate alone would leave the range reachable by asking for an ordinary backtest over
        the same dates.
      </p>
      <Button variant="danger" onClick={onUnlock} disabled={!canUnlock || unlocking}>
        {unlocking ? 'Unlocking…' : 'Unlock the holdout — this spends it'}
      </Button>
      {canUnlock ? (
        <p className="jmt-note-text">
          G1 to G11 all pass, so this may be opened. It can be run <strong>once</strong>.
        </p>
      ) : (
        // Disabled with a reason rather than hidden. A control that vanishes teaches nothing about
        // why it is unavailable, and this is the one the operator most wants to press.
        <p className="jmt-note-text">
          Unavailable until G1 to G11 all pass. G12 is checked last and only once, and unlocking
          early spends the one piece of evidence that cannot be replaced.
        </p>
      )}
    </div>
  );
}

function sentence(
  validation: ValidationResponse,
  passed: number,
  failed: number,
  unknown: number,
): string {
  if (validation.verdict === 'pass') {
    return 'Every criterion passes. That is the backtests declining to disqualify it, not a reason to deploy.';
  }
  if (failed === 0) {
    return `Nothing has failed and ${unknown} of ${passed + failed + unknown} criteria have no evidence yet. Unevaluated is not passed.`;
  }
  return `${failed} of ${passed + failed + unknown} criteria fail${unknown > 0 ? `, and ${unknown} could not be evaluated` : ''}.`;
}
